import { useLayoutEffect, useState } from 'react';
import { Group, Stack } from '@mantine/core';
import { useIntl } from 'react-intl';

import { addressFormMessages } from './address-form-messages';
import { FieldRenderer } from './field-renderer';
import { generateAddressFormSchema, groupInlineFields } from './schema-generator';
import { CustomerAutocomplete } from '../components/common/customer-autocomplete';

import type { AddressFieldSchema, AddressFormSchema, AddressType } from './types';
import type { Customer } from '../types/customer';
import type { Country, State, Address } from '../types/reference-data';

export type AddressFormReferenceData = {
  countries: Country[];
  states: State[];
  isLoadingCountries: boolean;
  isLoadingStates: boolean;
  sellerHomeCountry: string;
};

type AddressFormValue = Partial<Address>;

type AddressFormProps = {
  value: AddressFormValue;
  onChange?: (address: AddressFormValue) => void;
  onFieldChange?: (field: string, value: string) => void;
  countryCode: string;
  referenceData: AddressFormReferenceData;
  hiddenFields?: readonly string[];
  requiredFields?: readonly string[];
  disabledFields?: readonly string[];
  requireAll?: boolean;
  showAddressLine3?: boolean;
  disabled?: boolean;
  onBlur?: (address: AddressFormValue) => void;
  addressType?: AddressType;
  formProps?: React.ComponentPropsWithoutRef<'form'>;
  onCustomerSelect?: (customer: Customer) => void;
  labelTooltips?: Partial<Record<string, string>>;
};

const EMPTY_ARRAY: readonly string[] = Object.freeze([]);
const EMPTY_OBJECT: Partial<Record<string, string>> = Object.freeze({});

const getAutocompleteSection = (addressType: string) => `section-${addressType.toLowerCase()}`;

const preventSubmit = (event: React.FormEvent) => {
  event.preventDefault();
};

/**
 * Shared address form. Consumers inject reference data (countries, states,
 * loading flags, sellerHomeCountry) via the `referenceData` prop — the
 * component has no dependency on any consumer's state store or
 * data-fetching library. String copy is resolved via useIntl + the
 * library-owned addressFormMessages.
 */
export const AddressForm = ({
  value,
  onChange,
  onFieldChange,
  countryCode,
  referenceData,
  hiddenFields = EMPTY_ARRAY,
  requiredFields = EMPTY_ARRAY,
  disabledFields = EMPTY_ARRAY,
  requireAll = false,
  showAddressLine3 = true,
  disabled = false,
  onBlur,
  addressType = 'shipTo',
  formProps,
  onCustomerSelect,
  labelTooltips = EMPTY_OBJECT,
}: AddressFormProps) => {
  const [schema, setSchema] = useState<AddressFormSchema | null>(null);
  const intl = useIntl();

  useLayoutEffect(() => {
    setSchema(
      generateAddressFormSchema(countryCode, {
        addressType,
        hiddenFields: [...hiddenFields],
        requiredFields: [...requiredFields],
        requireAll,
        showAddressLine3,
      }),
    );
  }, [addressType, countryCode, hiddenFields, requiredFields, requireAll, showAddressLine3]);

  if (!schema) {
    return null;
  }

  const autocompleteSection = getAutocompleteSection(addressType);
  const groupedFields = groupInlineFields(schema.fields);

  const handleCustomerSelect = (customer: Customer) => {
    const updated: AddressFormValue = {
      ...value,
      name: customer.name ?? '',
      company: customer.company ?? '',
      line1: customer.line1 ?? '',
      line2: customer.line2 ?? '',
      city: customer.city ?? '',
      state: customer.state ?? '',
      postalCode: customer.postalCode ?? '',
      countryCode: customer.countryCode || value.countryCode || '',
      phone: customer.phone ?? '',
      email: customer.email ?? '',
    };
    onChange?.(updated);
    onCustomerSelect?.(customer);
  };

  const handleFieldChange = (fieldName: string, fieldValue: string) => {
    if (onFieldChange) {
      onFieldChange(fieldName, fieldValue);
      if (fieldName === 'countryCode') {
        onFieldChange('state', '');
      }
    } else if (onChange) {
      const updated: AddressFormValue = { ...value, [fieldName]: fieldValue };
      if (fieldName === 'countryCode') {
        updated.state = '';
      }
      onChange(updated);
    }
  };

  const handleFieldBlur = () => {
    if (onBlur) {
      onBlur(value);
    }
  };

  const renderSingleField = (field: AddressFieldSchema) => {
    const isFieldDisabled = disabled || disabledFields.includes(field.name);

    if (field.name === 'name') {
      const isRequired = requireAll || requiredFields.includes('name');
      return (
        <CustomerAutocomplete
          key={field.name}
          data-testid='address-field-name'
          disabled={isFieldDisabled}
          isRequired={isRequired}
          label={intl.formatMessage(addressFormMessages.name)}
          onChange={(val) => handleFieldChange(field.name, val)}
          onCustomerSelect={handleCustomerSelect}
          placeholder={intl.formatMessage(addressFormMessages.name)}
          value={(value.name ?? '') as string}
        />
      );
    }

    return (
      <FieldRenderer
        key={field.name}
        autocompleteSection={autocompleteSection}
        countries={referenceData.countries}
        countryCode={countryCode}
        disabled={isFieldDisabled}
        field={field}
        isLoadingCountries={referenceData.isLoadingCountries}
        isLoadingStates={referenceData.isLoadingStates}
        onBlur={handleFieldBlur}
        onChange={(val) => handleFieldChange(field.name, val)}
        sellerHomeCountry={referenceData.sellerHomeCountry}
        states={referenceData.states}
        labelTooltip={labelTooltips[field.name]}
        value={(value[field.name as keyof AddressFormValue] ?? '') as string}
      />
    );
  };

  const renderField = (item: AddressFieldSchema | AddressFieldSchema[], index: number) => {
    if (Array.isArray(item)) {
      return (
        <Group key={`group-${index}`} align='flex-start' gap='sm' grow>
          {item.map((field) => renderSingleField(field))}
        </Group>
      );
    }

    return renderSingleField(item);
  };

  return (
    <form onSubmit={preventSubmit} {...formProps}>
      <Stack gap='sm'>{groupedFields.map((field, index) => renderField(field, index))}</Stack>
    </form>
  );
};
